import type { ReactNode } from "react";

/**
 * An answer's text, rendered as the small slice of Markdown a model actually emits here.
 *
 * The previous renderer put every line in a paragraph verbatim, so `**a file name**` reached the
 * screen with its asterisks and a bulleted list arrived as a column of hyphens. What it must not do
 * instead is turn the string into HTML: this text is shaped by document content, so
 * `dangerouslySetInnerHTML` would put a corpus-influenced string into the DOM as markup. Building
 * elements keeps React's escaping intact — every span below is still a text node.
 *
 * Understood: paragraphs, bulleted and numbered lists, `**bold**`, `` `code` ``, `[label](url)` and
 * bare http(s) URLs. Deliberately not understood: underscore italics, which would eat the middle of
 * the file names this corpus is full of (`Credit_Summary_2016.pdf`), and raw HTML.
 */
export function AnswerText({ text }: { text: string }): JSX.Element {
  return <>{blocksIn(text).map(render)}</>;
}

type Block = { kind: "paragraph" | "bullets" | "numbers"; items: string[] };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/** Marked-up spans, in the order the alternation prefers them. */
const INLINE =
  /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]*[^\s).,;:!?])/g;

/** Group lines into blocks, so a run of list items becomes one list rather than several. */
function blocksIn(text: string): Block[] {
  const blocks: Block[] = [];
  for (const line of text.split("\n")) {
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    const kind = bullet ? "bullets" : numbered ? "numbers" : "paragraph";
    const item = (bullet?.[1] ?? numbered?.[1] ?? line).trim();
    if (!item) {
      continue; // a blank line only ends the run it follows; it is not a paragraph of its own
    }
    const open = blocks[blocks.length - 1];
    if (open?.kind === kind && kind !== "paragraph") {
      open.items.push(item);
    } else {
      blocks.push({ kind, items: [item] });
    }
  }
  return blocks;
}

function render(block: Block, index: number): JSX.Element {
  if (block.kind === "paragraph") {
    return (
      <p className="chat-turn__line" key={index}>
        {inline(block.items[0] ?? "")}
      </p>
    );
  }
  const items = block.items.map((item, at) => <li key={at}>{inline(item)}</li>);
  const List = block.kind === "bullets" ? "ul" : "ol";
  return (
    <List className="chat-turn__list" key={index}>
      {items}
    </List>
  );
}

/** Split one line into plain text and the few marked-up spans we understand. */
function inline(line: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let cursor = 0;
  for (const match of line.matchAll(INLINE)) {
    const [whole, bold, code, label, href, bare] = match;
    const at = match.index ?? 0;
    parts.push(line.slice(cursor, at));
    cursor = at + whole.length;
    if (bold) {
      parts.push(<strong key={cursor}>{bold}</strong>);
    } else if (code) {
      parts.push(<code key={cursor}>{code}</code>);
    } else if (label && href) {
      parts.push(link(href, label, cursor));
    } else if (bare) {
      parts.push(link(bare, bare, cursor));
    } else {
      parts.push(whole); // matched something we cannot build: show it as written, never drop it
    }
  }
  parts.push(line.slice(cursor));
  return parts;
}

/**
 * A link, but only to somewhere a browser may safely navigate.
 *
 * The href comes out of model output, so `javascript:` and `data:` have to be impossible rather
 * than unlikely. Anything that is not http(s) degrades to its own text, which still tells the
 * reader what was written.
 */
function link(href: string, label: string, key: number): ReactNode {
  if (!/^https?:\/\//i.test(href)) {
    return label;
  }
  return (
    <a className="chat-turn__link" href={href} target="_blank" rel="noopener noreferrer" key={key}>
      {label}
    </a>
  );
}
